import { mdiCheck, mdiClose, mdiDelete, mdiPencil, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import {
  defaultItem,
  OrderItemModelV2,
} from "../../../../../service/models/orders/order_item_model";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import ConsumablesSearchField from "../../components/consumables_search_field";
import moment from "moment";
import { UTCToLocalDate } from "../../../../../utils/date_util";

interface ExtraRowWidgetProps {
  extra?: OrderItemModelV2;
  onSubmit: (data: OrderItemModelV2) => void;
  edit: boolean;
  disabled: boolean;
  onDelete?: (i: OrderItemModelV2) => void;
}

const ExtraRowWidgetV2: React.FC<ExtraRowWidgetProps> = ({
  extra: initialExtra,
  onSubmit,
  edit,
  disabled,
  onDelete,
}) => {
  const { showToast } = useToast();
  const isAddMode = !initialExtra;

  const [extra, setExtra] = useState<OrderItemModelV2>({
    ...(initialExtra ?? defaultItem),
  });

  const [editable, setEditable] = useState<boolean>(isAddMode || edit);

  useEffect(() => {
    setEditable(isAddMode || edit);
  }, [edit, isAddMode]);

  const canEdit = editable && !disabled;

  const handleSubmit = () => {
    if (!extra.item_id || extra.qty < 1 || !extra.warehouse_id) {
      showToast({
        type: ToastType.warning,
        text: "Please select warehouse, item and qty",
      });
      return;
    }
    onSubmit(extra);
  };

  if (!extra.available_qty) {
    console.log("extra 1212 :", extra.item?.item_code, extra.qty, extra);
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="bg-white rounded-md shadow"
    >
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 p-5 md:items-center">
        <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
          <span className="md:hidden text-gray-400 text-xs">Warehouse</span>
          <WarehouseSearchField
            placeholder="Select Warehouse"
            value={extra.warehouse}
            disabled={!canEdit}
            onSelect={(data: WarehouseModel) => {
              setExtra((prev) => ({
                ...prev,
                warehouse_id: data.id,
                warehouse: data,
              }));
            }}
          />
        </div>

        <div className="flex flex-col gap-0.5">
          <span className="md:hidden text-gray-400 text-xs">Item Code</span>
          <input
            value={extra?.item?.item_code ?? ""}
            disabled
            placeholder="Item Code"
            type="text"
            className="p-1 rounded bg-gray-100 focus:outline-none w-full"
          />
        </div>

        <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
          <span className="md:hidden text-gray-400 text-xs">Item</span>
          <ConsumablesSearchField
            warehouse_id={extra.warehouse_id ?? ""}
            disabled={!canEdit}
            placeholder="Select Item"
            value={extra.item}
            onSelect={(item) => {
              setExtra((o) => ({
                ...o,
                item_id: item.id,
                item,
                available_qty: item.available_units ?? 0,
              }));
            }}
          />
        </div>

        <div className="flex flex-col gap-0.5 col-span-2 md:col-span-1">
          <span className="md:hidden text-gray-400 text-xs">Description</span>
          <input
            value={extra?.item?.description ?? ""}
            disabled
            placeholder="Item Description"
            type="text"
            className="p-1 rounded bg-gray-100 focus:outline-none w-full"
          />
        </div>

        <div className="space-y-1">
          <span className="md:hidden text-gray-400 text-xs">Quantity</span>
          <div className="grid grid-cols-4 items-center gap-1">
            <input
              value={extra.qty}
              onChange={(e) => {
                let v = 0;
                if (e.target.value && !isNaN(e.target.value as any)) {
                  v = Number(e.target.value);
                }

                if (extra.available_qty && extra.available_qty < v)
                  return showToast({
                    type: ToastType.error,
                    text: "Filled QTY is greater than AVAILABLE QTY",
                  });
                setExtra((ol) => ({ ...ol, qty: v }));
              }}
              disabled={!canEdit}
              type="number"
              className="text-right p-1 rounded bg-gray-100 focus:outline-none col-span-3"
            />
            <p className="font-semibold">{extra.item?.unit?.symbol ?? "--"}</p>
          </div>
          <span className="text-xs text-gray-500 block">
            Avail Units: {extra.available_qty}
          </span>
        </div>

        <div className="flex flex-col gap-0.5">
          <span className="md:hidden text-gray-400 text-xs">
            Installation Date
          </span>
          <input
            value={
              extra?.installation_date &&
              moment(extra.installation_date).format("YYYY-MM-DD")
            }
            onChange={(e) => {
              const newDate = e.target.value
                ? UTCToLocalDate(e.target.value)
                : undefined;
              setExtra((prev) => ({
                ...prev,
                installation_date: newDate,
              }));
            }}
            disabled={!canEdit}
            placeholder="Installation Date"
            type="date"
            className="p-1 rounded bg-gray-100 focus:outline-none w-full"
          />
        </div>

        <div className="flex gap-2 items-center justify-end col-span-2 md:col-span-1">
          {isAddMode ? (
            <button
              type="submit"
              className="flex gap-1 text-white text-sm bg-myPrimaryColor rounded-full px-2 py-1 hover:scale-110 transform duration-300"
            >
              <Icon path={mdiPlus} className="h-5 w-5" /> Add
            </button>
          ) : (
            !disabled && (
              <div className="flex gap-2 items-center">
                {editable ? (
                  <>
                    <button
                      type="submit"
                      className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                    >
                      <Icon path={mdiCheck} className="h-4 w-4" />
                    </button>
                    <div
                      onClick={() => {
                        setExtra(initialExtra);
                        setEditable(false);
                      }}
                      className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer"
                    >
                      <Icon path={mdiClose} className="h-4 w-4" />
                    </div>
                  </>
                ) : (
                  <div
                    onClick={() => setEditable(true)}
                    className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200 cursor-pointer"
                  >
                    <Icon path={mdiPencil} className="h-4 w-4" />
                  </div>
                )}

                {onDelete && (
                  <div
                    onClick={() => {
                      if (window.confirm("Are you sure?")) onDelete(extra);
                    }}
                    className="bg-red-200 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer"
                  >
                    <Icon path={mdiDelete} className="h-4 w-4" />
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </form>
  );
};

export default ExtraRowWidgetV2;
